import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Fehler, Laden, Leer } from '../components/common/States'
import { useProAnfragen, useProAnfrageEntscheiden } from '../hooks/useQualyz'
import { fehlerText } from '../lib/api'
import { formatDatum } from '../lib/lenses'
import type { ProAnfrage } from '../services/qualyz'

/**
 * Anfragen für den Pro-Zugang -- Kims Entscheidung an einer Stelle.
 *
 * Vorher war "Pro anfragen" ein `mailto:`-Link: Wer fragte, erfuhr niemand
 * (Kim, 23.09.2026). Jetzt steht jede Anfrage hier, mit Konto, Datum und
 * Nachricht, und zwei Knöpfen.
 */
export default function AnfragenPage() {
  const { isAdmin, isLoading } = useAuth()
  if (isLoading) return null
  if (!isAdmin) {
    return (
      <div className="q-page">
        <section className="q-card">
          <Leer text="Dieser Bereich ist Administratoren vorbehalten." />
        </section>
      </div>
    )
  }
  return <Liste />
}

const STATUS_TEXT: Record<ProAnfrage['status'], string> = {
  offen: 'offen',
  freigeschaltet: 'freigeschaltet',
  abgelehnt: 'abgelehnt',
}

function Liste() {
  const anfragen = useProAnfragen()
  const entscheiden = useProAnfrageEntscheiden()
  const [meldung, setMeldung] = useState<{ ok: boolean; text: string } | null>(null)

  const handeln = async (anfrage: ProAnfrage, freischalten: boolean) => {
    const adresse = anfrage.konto?.email ?? 'dieses Konto'
    const frage = freischalten
      ? `${adresse} den Pro-Zugang geben?`
      : `Anfrage von ${adresse} ablehnen?`
    if (!window.confirm(frage)) return
    try {
      await entscheiden.mutateAsync({ id: anfrage.id, freischalten })
      setMeldung({
        ok: true,
        text: freischalten ? `${adresse} ist jetzt Pro.` : `Anfrage von ${adresse} abgelehnt.`,
      })
    } catch (fehler) {
      setMeldung({ ok: false, text: fehlerText(fehler) })
    }
  }

  if (anfragen.isPending) {
    return (
      <div className="q-page">
        <section className="q-card">
          <Laden text="Lade die Anfragen …" />
        </section>
      </div>
    )
  }
  if (anfragen.isError) {
    return (
      <div className="q-page">
        <section className="q-card">
          <Fehler fehler={anfragen.error} nochmal={() => anfragen.refetch()} />
        </section>
      </div>
    )
  }

  const { anfragen: liste, offen } = anfragen.data
  return (
    <div className="q-page">
      <div className="q-placeholder-head">
        <span className={`q-badge ${offen ? 'q-badge--warn' : 'q-badge--ok'}`}>
          {offen ? `${offen} offen` : 'nichts offen'}
        </span>
      </div>

      {meldung && (
        <div className={`q-job-result ${meldung.ok ? 'is-ok' : 'is-err'}`} role="status">
          {meldung.text}
        </div>
      )}

      {liste.length === 0 ? (
        <section className="q-card">
          <Leer text="Bisher hat niemand nach dem Pro-Zugang gefragt." />
        </section>
      ) : (
        liste.map((a) => (
          <section key={a.id} className={`q-card q-anfrage${a.status === 'offen' ? ' is-offen' : ''}`}>
            <div className="q-card-head">
              <h3 className="q-card-title">{a.konto?.email ?? 'Konto entfernt'}</h3>
              <span className={`q-badge ${a.status === 'offen' ? 'q-badge--warn' : 'q-badge--ok'}`}>
                {STATUS_TEXT[a.status]}
              </span>
            </div>
            <p className="q-job-text">
              {a.konto?.name ? `${a.konto.name} · ` : ''}
              {a.gestellt_am ? `gefragt am ${formatDatum(a.gestellt_am.slice(0, 10))}` : ''}
              {a.konto?.seit ? ` · Konto seit ${formatDatum(a.konto.seit.slice(0, 10))}` : ''}
            </p>
            {a.nachricht && <p className="q-anfrage-nachricht">„{a.nachricht}"</p>}
            {a.status === 'offen' ? (
              <div className="q-job-foot">
                <span className="q-job-meta">Freischalten gilt sofort, beim nächsten Aufruf des Kontos.</span>
                <span className="q-anfrage-knoepfe">
                  <button
                    type="button"
                    className="q-btn q-btn-sm"
                    disabled={entscheiden.isPending}
                    onClick={() => handeln(a, false)}
                  >
                    Ablehnen
                  </button>
                  <button
                    type="button"
                    className="q-btn q-btn-primary q-btn-sm"
                    disabled={entscheiden.isPending}
                    onClick={() => handeln(a, true)}
                  >
                    Pro geben
                  </button>
                </span>
              </div>
            ) : (
              <p className="q-job-meta">
                {a.entschieden_am ? `entschieden am ${formatDatum(a.entschieden_am.slice(0, 10))}` : ''}
                {a.entschieden_von ? ` von ${a.entschieden_von}` : ''}
              </p>
            )}
          </section>
        ))
      )}
    </div>
  )
}
